import {gsap} from 'gsap';
import {is_mobile} from './utils.js';
import {initHorizontalScroll} from './horizontal-scroll.js';

// ─────────────────────────────────────────────────────────────────────────────
// About page — horizontal scroll timeline.
//
// Il pin orizzontale, lo snap e la timeline GSAP sono delegati al modulo
// generico `horizontal-scroll`. Qui restano le parti specifiche dell'about:
//   - dwell sul pannello timeline (items.length × ITEM_SCROLL)
//   - animazione divider, dot, items, years, descPanels via onUpdate
//   - slider e nav mobile per scorrere gli item su touch
//   - click handler su item / bottone start
// ─────────────────────────────────────────────────────────────────────────────
function initAboutHorizontalScroll(lenis) {
    const timelinePanel = document.querySelector('.about-timeline.js-h-panel');
    if (!timelinePanel) {
        return null;
    }
    const outer = document.querySelector('.js-h-outer');
    const wrapper = outer ? outer.querySelector('.js-h-scroll') : null;
    if (!outer || !wrapper) {
        return null;
    }

    const panels = Array.from(wrapper.querySelectorAll(':scope > .js-h-panel'));
    const timelineIdx = panels.indexOf(timelinePanel);
    if (timelineIdx < 0) {
        return null;
    }

    const items = gsap.utils.toArray('.about-timeline__item', timelinePanel);
    const years = gsap.utils.toArray('.about-timeline__year', timelinePanel);
    const descPanels = gsap.utils.toArray('.about-timeline__panel', timelinePanel);
    const divider = timelinePanel.querySelector('.about-timeline__divider');
    const dot = timelinePanel.querySelector('.about-timeline__dot');

    const ITEM_SCROLL = is_mobile() ? 400 : 1400;
    const MOBILE_BP = 992;

    let lastActiveIdx = -1;

    // ── Mobile slider (DOM) ───────────────────────────────────────────────────
    function buildMobileSlider() {
        if (timelinePanel._sliderData) {
            return null;
        }

        const inner = timelinePanel.querySelector('.about-timeline__inner');
        if (!inner) {
            return null;
        }
        const track = inner.querySelector('.about-timeline__track');
        const panelsEl = inner.querySelector('.about-timeline__panels');
        const dividerEl = inner.querySelector('.about-timeline__divider');
        if (!track) {
            return null;
        }

        const slider = document.createElement('div');
        slider.className = 'about-timeline__slider';

        const sliderTrack = document.createElement('div');
        sliderTrack.className = 'about-timeline__slider-track';
        slider.appendChild(sliderTrack);

        items.forEach((item, i) => {
            const slide = document.createElement('div');
            slide.className = 'about-timeline__slide';
            slide.appendChild(item);
            if (descPanels[i]) {
                slide.appendChild(descPanels[i]);
            }
            sliderTrack.appendChild(slide);
        });

        inner.insertBefore(slider, track);
        track.remove();
        if (panelsEl) {
            panelsEl.remove();
        }

        let slideIndex = 0;

        function goToSlide(i, animate) {
            slideIndex = Math.max(0, Math.min(i, items.length - 1));
            sliderTrack.style.transition = animate === false ? 'none' : '';
            sliderTrack.style.transform = 'translate3d(' + (-slideIndex * window.innerWidth) + 'px,0,0)';
        }

        goToSlide(0);

        timelinePanel._sliderData = {
            track, panelsEl, dividerEl,
            sliderTrack: sliderTrack,
            goToSlide: goToSlide,
            getSlideIndex: function () {
                return slideIndex;
            },
            slideCount: items.length
        };
    }

    function destroyMobileSlider() {
        if (!timelinePanel._sliderData) {
            return null;
        }

        const inner = timelinePanel.querySelector('.about-timeline__inner');
        const slider = inner.querySelector('.about-timeline__slider');
        const {track, panelsEl, dividerEl} = timelinePanel._sliderData;

        if (slider) {
            items.forEach((item, i) => {
                track.appendChild(item);
                if (descPanels[i] && panelsEl) {
                    panelsEl.appendChild(descPanels[i]);
                }
            });
            slider.remove();
        }

        inner.insertBefore(track, inner.firstChild);
        if (panelsEl) {
            inner.appendChild(panelsEl);
        }
        if (dividerEl) {
            dividerEl.style.display = '';
            inner.appendChild(dividerEl);
        }

        timelinePanel._sliderData = null;
    }

    // ── Mobile navigator: swipe verticale → sezioni + slide timeline ─────────
    function buildMobileNav() {
        if (timelinePanel._mobileNav) {
            return null;
        }
        const mobileSections = Array.from(wrapper.querySelectorAll(':scope > section'));
        const TIMELINE_IDX = mobileSections.indexOf(timelinePanel);
        const gotoStartBtn = outer.querySelector('.hs__goto-start');
        const scrollSvg = document.querySelector('.hs__scroll-global');
        let sectionIndex = 0;
        let isAtFooter = false;

        function onScroll() {
            isAtFooter = window.scrollY > outer.offsetHeight * 0.3;
            if (scrollSvg) {
                scrollSvg.classList.toggle('--hidden', isAtFooter || sectionIndex === mobileSections.length - 1);
            }
        }

        window.addEventListener('scroll', onScroll, {passive: true});
        onScroll();

        function goToSection(i, animate) {
            sectionIndex = Math.max(0, Math.min(i, mobileSections.length - 1));
            wrapper.style.transition = animate === false ? 'none' : '';
            wrapper.style.transform = 'translate3d(' + (-sectionIndex * window.innerWidth) + 'px,0,0)';
            const isLast = sectionIndex === mobileSections.length - 1;
            outer.classList.toggle('is-last-panel', isLast);
            if (gotoStartBtn) {
                gotoStartBtn.classList.toggle('active', isLast);
            }
            if (scrollSvg) {
                scrollSvg.classList.toggle('--hidden', isLast || isAtFooter);
            }
        }

        let winStartY = 0, winStartX = 0, winGesture = null;
        let dragEl = null, dragBase = 0;
        let lastDy = 0, lastT = 0, velocity = 0;

        function snapBack() {
            const sd = timelinePanel._sliderData;
            if (dragEl === wrapper) {
                goToSection(sectionIndex, true);
            } else if (sd) {
                sd.goToSlide(sd.getSlideIndex(), true);
            }
        }

        function onWinStart(e) {
            winStartY = e.touches[0].clientY;
            winStartX = e.touches[0].clientX;
            winGesture = null;
            dragEl = null;
            lastDy = 0;
            velocity = 0;
            lastT = e.timeStamp || performance.now();
        }

        function onWinMove(e) {
            const dy = e.touches[0].clientY - winStartY;
            const dx = e.touches[0].clientX - winStartX;
            if (winGesture === null && (Math.abs(dy) > 6 || Math.abs(dx) > 6)) {
                if (Math.abs(dy) < Math.abs(dx)) {
                    winGesture = 'pass';
                    return;
                }
                const sd = timelinePanel._sliderData;
                const fwd = dy < 0;
                let canAct;
                if (fwd) {
                    canAct = !isAtFooter && (
                        (sectionIndex === TIMELINE_IDX && sd && sd.getSlideIndex() < sd.slideCount - 1) ||
                        sectionIndex < mobileSections.length - 1
                    );
                } else {
                    canAct = isAtFooter ||
                        sectionIndex > 0 ||
                        (sectionIndex === TIMELINE_IDX && sd && sd.getSlideIndex() > 0);
                }
                winGesture = canAct ? 'hijack' : 'pass';
                if (winGesture === 'hijack') {
                    if (sectionIndex === TIMELINE_IDX && sd) {
                        dragEl = sd.sliderTrack;
                        dragBase = -sd.getSlideIndex() * window.innerWidth;
                    } else {
                        dragEl = wrapper;
                        dragBase = -sectionIndex * window.innerWidth;
                    }
                    dragEl.style.transition = 'none';
                }
            }
            if (winGesture === 'hijack' && dragEl) {
                e.preventDefault();
                e.stopPropagation();
                const now = e.timeStamp || performance.now();
                const dt = now - lastT;
                if (dt > 0) {
                    velocity = (dy - lastDy) / dt;
                }
                lastDy = dy;
                lastT = now;
                let offset = dy;
                if ((dragBase + offset) > 0) {
                    offset = offset * 0.3;
                }
                dragEl.style.transform = 'translate3d(' + (dragBase + offset) + 'px,0,0)';
            }
        }

        function onWinEnd(e) {
            if (winGesture !== 'hijack' || !dragEl) {
                winGesture = null;
                dragEl = null;
                return null;
            }
            const dy = e.changedTouches[0].clientY - winStartY;
            const sd = timelinePanel._sliderData;
            const vw = window.innerWidth;
            const distThresh = vw * 0.18;
            const velThresh = 0.4;
            const goNext = dy < -distThresh || velocity < -velThresh;
            const goPrev = dy > distThresh || velocity > velThresh;

            dragEl.style.transition = '';

            if (goNext) {
                if (sectionIndex === TIMELINE_IDX && sd && sd.getSlideIndex() < sd.slideCount - 1) {
                    sd.goToSlide(sd.getSlideIndex() + 1, true);
                } else if (sectionIndex < mobileSections.length - 1) {
                    goToSection(sectionIndex + 1, true);
                } else {
                    snapBack();
                }
            } else if (goPrev) {
                if (isAtFooter) {
                    goToSection(mobileSections.length - 1, true);
                    lenis.scrollTo(0, {duration: 0.8});
                } else if (sectionIndex === TIMELINE_IDX && sd && sd.getSlideIndex() > 0) {
                    sd.goToSlide(sd.getSlideIndex() - 1, true);
                } else if (sectionIndex > 0) {
                    const prevIdx = sectionIndex - 1;
                    goToSection(prevIdx, true);
                    if (prevIdx === TIMELINE_IDX && sd) {
                        sd.goToSlide(sd.slideCount - 1, true);
                    }
                } else {
                    snapBack();
                }
            } else {
                snapBack();
            }

            winGesture = null;
            dragEl = null;
        }

        window.addEventListener('touchstart', onWinStart, {passive: true});
        window.addEventListener('touchmove', onWinMove, {passive: false});
        window.addEventListener('touchend', onWinEnd, {passive: true});

        timelinePanel._mobileNav = {
            onWinStart, onWinMove, onWinEnd, onScroll, goToSection,
            getSectionIndex: function () {
                return sectionIndex;
            }
        };
    }

    function destroyMobileNav() {
        if (!timelinePanel._mobileNav) {
            return null;
        }
        const nav = timelinePanel._mobileNav;
        window.removeEventListener('touchstart', nav.onWinStart);
        window.removeEventListener('touchmove', nav.onWinMove);
        window.removeEventListener('touchend', nav.onWinEnd);
        window.removeEventListener('scroll', nav.onScroll);
        wrapper.style.transform = '';
        outer.classList.remove('is-last-panel');
        timelinePanel._mobileNav = null;
    }

    // ── Reset stati items/year/descPanels ────────────────────────────────────
    function clearItemsState() {
        if (lastActiveIdx !== -1) {
            descPanels.forEach(p => {
                gsap.killTweensOf(p);
                p.classList.remove('is-active');
                gsap.set(p, {clearProps: 'all'});
            });
            lastActiveIdx = -1;
        }
        items.forEach(item => {
            item.classList.remove('is-active');
            gsap.set(item, {opacity: 0.35});
        });
        years.forEach(y => y.classList.remove('is-active'));
    }

    // Stato iniziale divider/dot
    if (divider) {
        gsap.set(divider, {scaleX: 0, transformOrigin: 'left center'});
    }
    if (dot) {
        gsap.set(dot, {opacity: 0, scale: 0});
    }

    // ── Bootstrap horizontal scroll (delegato al generico) ──────────────────
    const handle = initHorizontalScroll({
        lenis,
        outer,
        wrapper,
        panels,
        useGenericMobile: false,
        dwells: items.length ? {[timelineIdx]: items.length * ITEM_SCROLL} : {},
        onUpdate(self, ctx) {
            const {phase, localProg, phases} = ctx;

            // Frazione di arrivo sulla timeline (= fine della transition hero→timeline)
            const transToTimeline = phases.find(p => p.type === 'transition' && p.toIndex === timelineIdx);
            const p1End = transToTimeline ? transToTimeline.f1 : 0;
            const p1 = p1End > 0 ? Math.min(self.progress / p1End, 1) : 1;

            if (divider) {
                gsap.set(divider, {scaleX: p1});
            }
            if (dot) {
                const reveal = Math.max(0, (p1 - 0.75) / 0.25);
                gsap.set(dot, {scale: reveal, opacity: reveal});
            }

            if (!items.length) {
                return null;
            }

            const inDwell = phase.type === 'dwell' && phase.panelIndex === timelineIdx;
            if (!inDwell) {
                clearItemsState();
                return null;
            }

            const activeIndex = Math.min(Math.floor(localProg * items.length), items.length - 1);
            items.forEach((item, i) => {
                const isActive = i === activeIndex;
                item.classList.toggle('is-active', isActive);
                gsap.set(item, {opacity: isActive ? 1 : 0.35});
            });
            years.forEach((y, i) => y.classList.toggle('is-active', i === activeIndex));

            if (activeIndex !== lastActiveIdx) {
                if (lastActiveIdx >= 0 && descPanels[lastActiveIdx]) {
                    gsap.killTweensOf(descPanels[lastActiveIdx]);
                    descPanels[lastActiveIdx].classList.remove('is-active');
                    gsap.set(descPanels[lastActiveIdx], {clearProps: 'all'});
                }
                if (descPanels[activeIndex]) {
                    gsap.killTweensOf(descPanels[activeIndex]);
                    descPanels[activeIndex].classList.add('is-active');
                    gsap.fromTo(descPanels[activeIndex],
                        {x: 28, opacity: 0},
                        {x: 0, opacity: 1, duration: 0.5, ease: 'power2.out'}
                    );
                }
                lastActiveIdx = activeIndex;
            }
        }
    });

    // ── Sync slider/nav mobile (indipendenti dal pin desktop del generico) ──
    function syncMobile() {
        if (window.innerWidth < MOBILE_BP) {
            const nav = timelinePanel._mobileNav;
            const savedSectionIdx = nav ? nav.getSectionIndex() : 0;
            buildMobileSlider();
            if (!timelinePanel._mobileNav) {
                buildMobileNav();
            } else if (nav) {
                nav.goToSection(savedSectionIdx);
            }
        } else {
            destroyMobileNav();
            destroyMobileSlider();
        }
    }

    syncMobile();
    window.addEventListener('resize', syncMobile);

    // Click "Scopri la nostra storia" su mobile: avanza alla sezione successiva
    // (su desktop la navigazione è gestita da horizontal-scroll.js via ScrollTrigger)
    const startBtn = document.querySelector('.js-start-horizontal');
    if (startBtn) {
        startBtn.addEventListener('click', () => {
            if (window.innerWidth >= MOBILE_BP) {
                return null;
            }
            const nav = timelinePanel._mobileNav;
            if (nav) {
                nav.goToSection(1, true);
            }
        });
    }

    // Reset del nav mobile custom on click goto-start
    // (lo scroll a 0 e il toggle .active sono già gestiti da horizontal-scroll.js)
    const gotoStartBtn = outer.querySelector('.hs__goto-start');
    if (gotoStartBtn) {
        gotoStartBtn.addEventListener('click', () => {
            const nav = timelinePanel._mobileNav;
            if (nav) {
                nav.goToSection(0, true);
            }
        });
    }

    // ── Click su item → slide (mobile) o scroll alla posizione (desktop) ─────
    items.forEach((item, i) => {
        item.style.cursor = 'pointer';
        item.addEventListener('click', () => {
            const sd = timelinePanel._sliderData;
            if (sd) {
                sd.goToSlide(i);
                return null;
            }
            if (!handle || !handle.st) {
                return null;
            }
            const dwell = handle.phases.find(p => p.type === 'dwell' && p.panelIndex === timelineIdx);
            if (!dwell) {
                return null;
            }
            const itemFrac = (i + 0.5) / items.length;
            const targetFrac = dwell.f0 + itemFrac * (dwell.f1 - dwell.f0);
            const targetScroll = handle.st.start + (handle.st.end - handle.st.start) * targetFrac;
            lenis.scrollTo(targetScroll, {duration: 1.2, lerp: 0.08});
        });
    });

    // ── Wheel desktop nella timeline: 1 scatto = 1 pannello ─────────────────
    // Dentro la zona timeline gestiamo noi la rotella (stepping discreto sugli
    // item), fuori lasciamo fare al wheel-hijack generico di horizontal-scroll.
    // Capture + stopPropagation: il nostro handler precede lenis e il generico.
    if (handle && items.length) {
        const DEBOUNCE = 30;       // fonde il burst di un singolo scatto/swipe
        const STEP_DURATION = 0.2; // step item↔item: scattante
        const EDGE_DURATION = 1.0; // entrata/uscita timeline: come le altre sezioni
        const E = 0.002;
        let lastTime = 0;
        let animating = false;
        let curIdx = -1;

        function getScrollY() {
            return lenis && typeof lenis.scroll === 'number' ? lenis.scroll : window.scrollY;
        }

        function model() {
            const st = handle.st;
            if (!st) {
                return null;
            }
            const phases = handle.phases;
            const di = phases.findIndex(p => p.type === 'dwell' && p.panelIndex === timelineIdx);
            if (di < 0) {
                return null;
            }
            const dwell = phases[di];
            const before = phases[di - 1];
            const after = phases[di + 1];
            const stops = [];
            if (before) {
                stops.push({frac: before.f0, item: false});
            }
            for (let i = 0; i < items.length; i++) {
                stops.push({frac: dwell.f0 + ((i + 0.5) / items.length) * (dwell.f1 - dwell.f0), item: true});
            }
            if (after) {
                stops.push({frac: after.f1, item: false});
            }
            return {st, total: st.end - st.start, stops};
        }

        function onWheel(e) {
            const st = handle.st;
            if (!st || !st.isActive) {
                return null;
            }
            const m = model();
            if (!m || !m.stops.length) {
                return null;
            }
            const progress = m.total > 0 ? (getScrollY() - st.start) / m.total : 0;
            const lo = m.stops[0].frac;
            const hi = m.stops[m.stops.length - 1].frac;
            // Fuori dalla zona timeline: lascia il wheel-hijack generico.
            if (progress < lo - E || progress > hi + E) {
                return null;
            }

            const now = performance.now();
            if (now - lastTime < DEBOUNCE) {
                e.preventDefault();
                e.stopPropagation();
                return null;
            }

            if (!animating) {
                let best = 0, bd = Infinity;
                m.stops.forEach((s, i) => {
                    const d = Math.abs(s.frac - progress);
                    if (d < bd) {
                        bd = d;
                        best = i;
                    }
                });
                curIdx = best;
            }

            const next = curIdx + (e.deltaY > 0 ? 1 : -1);
            // Ai bordi della zona: lascia uscire (il generico cambia sezione).
            if (next < 0 || next > m.stops.length - 1) {
                return null;
            }

            e.preventDefault();
            e.stopPropagation();
            lastTime = now;

            const intra = m.stops[curIdx].item && m.stops[next].item;
            const duration = intra ? STEP_DURATION : EDGE_DURATION;
            curIdx = next;
            animating = true;

            const target = st.start + m.stops[next].frac * m.total;
            const done = () => {
                animating = false;
            };

            if (lenis && typeof lenis.scrollTo === 'function') {
                lenis.scrollTo(target, {duration: duration, lock: true, onComplete: done});
            } else {
                window.scrollTo({top: target, behavior: 'smooth'});
                setTimeout(done, duration * 1000);
            }
        }

        window.addEventListener('wheel', onWheel, {passive: false, capture: true});
    }

    return handle;
}

export {initAboutHorizontalScroll};
